import { useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useMutationWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import queryString from 'query-string';
import type { UseMutationOptions, UseMutationResult } from 'react-query';
import { useParams } from 'react-router-dom';
import { useGetSupplierItemsApi } from './use-get-supplier-items-api';

const endpoint = 'catalog/v1/legacy-products/upload-csv';
export const uploadItemsCSVFilesEndpointIdentifier = endpoint;

type TData = Record<string, any>;
type TError = AxiosError;

type TQueryKey = {
  supplierId: string;
  restaurantId: string;
  branchId: string;
  subEntityType: string;
};

type TVariables = {
  body: FormData;
};

type Options = Omit<UseMutationOptions<TData, TError, TVariables, unknown>, 'mutationFn'>;
type ReturnType = UseMutationResult<TData, TError, TVariables>;

function constructEndpoint(supplierId: string, query: TQueryKey) {
  const stringifiedQuery = queryString.stringify(query, { arrayFormat: 'bracket' });
  return `${endpoint}/${supplierId}?${stringifiedQuery}`;
}

export function useUploadSupplierItemsCSVFileApi(options?: Options): ReturnType {
  const { restaurantId, branchId, supplierId } = useParams<{
    supplierId: string;
    restaurantId: string;
    branchId: string;
  }>();

  const { refetch: refetchSupplierItems } = useGetSupplierItemsApi({ enabled: false });
  const toast = useCustomToast();
  const fullEndpoint = constructEndpoint(supplierId, {
    supplierId,
    restaurantId,
    branchId: branchId,
    subEntityType: 'branch',
  });

  const mutationFn = async ({ body }: TVariables) => {
    try {
      const { data } = await http.post(fullEndpoint, body, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (data?.status) {
        toast({
          description: 'the file is waiting in queue, will be uploaded shortly.',
          status: 'error',
        });
      }

      if (data?.aborted) {
        toast({
          description: data?.unsuccessful?.errors[0]?.value?.message ?? 'unexpected error',
          status: 'error',
        });
      }

      if (!data?.aborted && !data?.status) {
        toast({
          description: 'CSV file has been uploaded successfully.',
          status: 'success',
        });
      }

      // Refetch supplier items
      refetchSupplierItems();

      return data;
    } catch (error: any) {
      if (!error.response.status || !error.response.data.message) throw error;

      toast({
        description: error.response.data.message,
        status: 'error',
      });

      throw error;
    }
  };

  return useMutationWrapper(mutationFn, { mutationKey: [endpoint], ...options });
}
